(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = Object.freeze({create: factory});
  else {
    root.CoiloverModelFactory=Object.freeze({create:factory});
    try {
      const node = document.querySelector('[data-coil-application]');
      if (!node) throw new Error('Missing application data');
      root.CoiloverModel = factory(JSON.parse(node.textContent));
    } catch (error) {
      const page = document.querySelector('[data-coilover-preview]');
      if (page) page.textContent = 'This configuration could not be loaded. Please refresh or contact CEIKA.';
    }
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function (application) {
  'use strict';

  const controls = typeof module === 'object' && module.exports ? require('./ceika-coilover-controls.js') : globalThis.CoiloverControls;
  if (!controls) throw new TypeError('Missing coilover option controls');
  let runtimeControls;
  let allNative;
  const deepFreeze = value => {
    Object.values(value).forEach(child => {
      if (child && typeof child === 'object' && !Object.isFrozen(child)) deepFreeze(child);
    });
    return Object.freeze(value);
  };

  let catalog = deepFreeze({
    productId: null,
    currency: 'USD',
    priceContext: 'base_usd_preview',
    orderingEnabled: false,
    modes: ['guided', 'expert'],
    intendedUses: ['daily', 'spirited', 'track', 'rally'],
    setups: ['comfort', 'sport', 'pro_racing', 'custom'],
    dampers: ['1m', '2m'],
    colors: ['blue', 'orange', 'green', 'silver', 'red'],
    customValving: ['standard', 'pro'],
    frontMounts: ['rubber', 'pillowball'],
    heights: { lowered: 0, oem: 150, extra_low: 150, lifted: 230 },
    prices: { customProValving: 200, frontPillowball: 50, frontHelpers: 75, frontRearHelpers: 150 },
    helperScopes: ['none', 'front', 'front_rear'],
    springPairs: {},
    springOptions: {},
    hoseLengthsCm: [20, 30, 40, 50, 60],
    cores: [],
    recommendationSetups: { daily: 'comfort', spirited: 'sport', track: 'pro_racing', rally: 'custom' }
  });

  if (!application) throw new TypeError('Application data is required');
  const springCatalogue = typeof module === 'object' && module.exports ? require('./ceika-spring-catalogue.js') : globalThis.CeikaSpringCatalogue;
  if (!springCatalogue) throw new TypeError('Spring catalogue is unavailable');
  application = springCatalogue.restrict(application);
  if (application) {
    if (!Array.isArray(application.variants)) throw new TypeError('Invalid variant payload');
    const originalId=String(application.profile?.pricing?.basePriceVariantId);
    const legacy=application.variants.filter(v=>v.setup==='Legacy kit'||v.damper==='Existing configuration');
    if(legacy.length){
      if(legacy.length!==1 || String(legacy[0].variantId)!==originalId || legacy[0].setup!=='Legacy kit' || legacy[0].damper!=='Existing configuration')throw new TypeError('Unverified legacy variant');
      if(Object.hasOwn(application.previewPricing?.baseVariants||{},originalId) || (application.previewPricing?.variants||[]).some(v=>String(v.variantId)===originalId))throw new TypeError('Legacy variant in native pricing');
      application={...application,variants:application.variants.filter(v=>v!==legacy[0])};
    }
    if(new Set(application.variants.map(v=>String(v.variantId))).size!==application.variants.length)throw new TypeError('Duplicate native variant');
    const compiled = controls.apply(application.profile, application.policy || {}, application.variants);
    const p = compiled.profile;
    const separateMountsRequired = ['front','rear'].some(axle=>p.axles[axle].mounts.default==='pillowball');
    if(separateMountsRequired && p.pricing.mountPricingMode!=='separate_per_axle_v1')throw new TypeError('Separate mount pricing connection required');
    runtimeControls = compiled.runtime;
    const reviewFallback=p.manualReview?.displayAllUnresolvedOptions===true;
    if (!p || ![1,2].includes(p.schemaVersion) || p.purchaseEnabled !== false || (!reviewFallback && p.readiness.status !== 'ready_for_draft_test') ||
        (!reviewFallback && p.readiness.issues.length) || p.productId !== application.productId || !Array.isArray(application.variants) ||
        application.previewPricing.productId !== p.productId) throw new TypeError('Invalid application profile');
    const native = application.variants.map(v => {
      const setup = v.setup === 'Pro Racing' ? 'pro_racing' : v.setup.toLowerCase();
      const damper = v.damper.slice(-2).toLowerCase();
      const key = setup + '_' + damper;
      const base = application.previewPricing.baseVariants[v.variantId];
      if (!application.profile.configuration.cores.includes(key) || !Number.isSafeInteger(base) || base <= 0) throw new TypeError('Unmapped native variant');
      if(separateMountsRequired){
        const expected=Math.round((p.pricing.basePriceUsd+(damper==='2m'?p.pricing.type2mUpgradeUsd:0)+(setup==='pro_racing'?p.pricing.proValvingUsd:0))*100);
        if(base!==expected)throw new TypeError('Mount-inclusive native base is not supported');
      }
      return {key,setup,damper,variantId:v.variantId,priceUsd:base/100};
    });
    allNative = native.slice();
    native.splice(0,native.length,...native.filter(v=>p.configuration.cores.includes(v.key)));
    if (!native.length) throw new TypeError('No enabled coilover kit configurations');
    if (native.length !== p.configuration.cores.length || new Set(native.map(v=>v.key)).size !== native.length ||
        new Set(native.map(v=>v.variantId)).size !== native.length) throw new TypeError('Incomplete native variants');
    const pairs = {};
    const keys = {standard:'standard_pair_v1',softer:'softer_pair_v1',firmer:'firmer_pair_v1'};
    for (const pair of p.springPairs.presets) if (keys[pair.key]) pairs[keys[pair.key]]={frontKgMm:pair.frontKgMm,rearKgMm:pair.rearKgMm};
    const defaults={frontKgMm:p.axles.front.spring.standardRateKgMm,rearKgMm:p.axles.rear.spring.standardRateKgMm};
    for(const ax of ['front','rear'])if(defaults[ax+'KgMm']==null&&!reviewFallback && !['torsion_bar','not_supplied','no_coil_spring'].includes(p.axles[ax].layout.springPosition))throw new TypeError('Missing spring specification');
    if(!pairs.standard_pair_v1)pairs.standard_pair_v1={...defaults};
    catalog = deepFreeze({...catalog,productId:p.productId,vehicle:p.vehicle,legacyVariantId:p.pricing.basePriceVariantId,
      allCores:allNative,customValving:runtimeControls.customValving,reservoirMounts:runtimeControls.reservoirMounts,
      colors:runtimeControls.colors,customSpringsEnabled:runtimeControls.customSpringsEnabled && ['front','rear'].some(ax=>!p.springCatalogue.axles[ax].defaultOnly && p.axles[ax].spring.choices.length),
      setups:[...new Set(native.map(v=>v.setup))],dampers:[...new Set(native.map(v=>v.damper))],cores:native,
      mountDetails:Object.fromEntries(['front','rear'].map(axle=>[axle,{
        architecture:p.axles[axle].layout.architecture,
        options:p.axles[axle].mounts.options.map(o=>({...o,camberAdjustable:o.key==='pillowball' ?
          (p.axles[axle].layout.architecture==='macpherson' ? true : p.axles[axle].layout.architecture==='unknown' ? null : false) : o.camberAdjustable}))
      }])),
      reviewFallback,springCatalogue:p.springCatalogue,
      springAxles:['front','rear'].filter(ax=>p.axles[ax].spring.choices.length>0),
      frontMounts:p.axles.front.mounts.options.map(o=>o.key),rearMounts:p.axles.rear.mounts.options.map(o=>o.key),
      defaultFrontMount:p.axles.front.mounts.default,defaultRearMount:p.axles.rear.mounts.default,
      includedFrontMount:false,includedRearMount:false,
      helperScopes:p.helperScopes,heights:Object.fromEntries(p.configuration.heightChoices.map(k=>[k,p.pricing.heightSurchargesUsd[k]])),
      springPairs:pairs,springOptions:{revision:'application-v2',standard:defaults,
        frontRatesKgMm:p.springCatalogue.axles.front.defaultOnly?[]:p.axles.front.spring.choices.map(o=>o.rateKgMm),rearRatesKgMm:p.springCatalogue.axles.rear.defaultOnly?[]:p.axles.rear.spring.choices.map(o=>o.rateKgMm)},
      hoseLengthsCm:runtimeControls.hoseLengthsCm});
  }

  function initialState() {
    const core = catalog.cores.find(c=>c.key==='sport_1m') || catalog.cores[0];
    return {
      mode: 'guided', intendedUse: null, setup: core.setup, damper: core.damper,
      customValving: 'standard', height: 'lowered', frontMount: catalog.defaultFrontMount || 'rubber', rearMount: catalog.defaultRearMount || 'no_supplied_mount',
      helperScope: 'none', reservoirMount: core.damper==='2m' ? catalog.reservoirMounts[0] : 'not_applicable', hoseLengthCm: null,
      springPair: 'standard_pair_v1', frontSpringRateKgMm: null,
      rearSpringRateKgMm: null, springLinked: false, color: catalog.colors[0], notes: ''
    };
  }

  const validEnum = (values, value) => values.includes(value);
  const has = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
  const validRate = value => typeof value === 'number' && Number.isFinite(value) && value > 0;

  function createSpringLinkPolicy(springOptions) {
    return deepFreeze({standard:{...springOptions.standard},linkedPairs:[],frontRatesKgMm:[],rearRatesKgMm:[],pairForFront:()=>null,pairForRear:()=>null,matchesStandard:()=>false,relink:rates=>({canLink:false,rates:{...rates},reason:'independent_axles'}),describe:()=>({enabled:false,matchesStandard:false,canLink:false,reason:'independent_axles',relinkedRates:null})});
  }

  const springLinkPolicy = createSpringLinkPolicy(catalog.springOptions);

  function retainedCustomRate(axle, rate) {
    const supplied = catalog.springCatalogue.axles[axle].includedDefault;
    return (supplied && rate === supplied.rateKgMm) || catalog.springOptions[axle+'RatesKgMm'].includes(rate) ? rate : null;
  }

  function resolveSpringRates(selection) {
    if (!selection || typeof selection !== 'object') throw new TypeError('Unsupported spring selection');
    if (selection.springPair === 'custom') {
      if (!catalog.customSpringsEnabled) throw new TypeError('Custom spring selection is disabled');
      const frontKgMm = catalog.springCatalogue.axles.front.defaultOnly?.rateKgMm ?? selection.frontSpringRateKgMm;
      const rearKgMm = catalog.springCatalogue.axles.rear.defaultOnly?.rateKgMm ?? selection.rearSpringRateKgMm;
      if (!(catalog.springCatalogue.axles.front.defaultOnly || frontKgMm===null || (validRate(frontKgMm) && retainedCustomRate('front',frontKgMm)===frontKgMm)) ||
          !(catalog.springCatalogue.axles.rear.defaultOnly || rearKgMm===null || (validRate(rearKgMm) && retainedCustomRate('rear',rearKgMm)===rearKgMm))) {
        throw new TypeError('Unsupported custom spring rates');
      }
      return { frontKgMm, rearKgMm };
    }
    if (!has(catalog.springPairs, selection.springPair) ||
        selection.frontSpringRateKgMm != null || selection.rearSpringRateKgMm != null) {
      throw new TypeError('Unsupported spring selection');
    }
    return { ...catalog.springPairs[selection.springPair] };
  }

  function assertState(state) {
    if (!state || !validEnum(catalog.modes, state.mode) ||
      !(state.intendedUse === null || validEnum(catalog.intendedUses, state.intendedUse)) ||
      !validEnum(catalog.setups, state.setup) || !validEnum(catalog.dampers, state.damper) ||
      !catalog.cores.some(core => core.setup === state.setup && core.damper === state.damper) ||
      !validEnum(catalog.customValving, state.customValving) ||
      !has(catalog.heights, state.height) || !validEnum(catalog.frontMounts, state.frontMount) ||
      !validEnum(catalog.helperScopes, state.helperScope) ||
      (catalog.rearMounts && !catalog.rearMounts.includes(state.rearMount || catalog.defaultRearMount)) ||
      !(state.springLinked === undefined || typeof state.springLinked === 'boolean') ||
      !validEnum(catalog.colors, state.color) || typeof state.notes !== 'string' || state.notes.length > 500 ||
      !(state.damper === '2m' ? catalog.reservoirMounts.includes(state.reservoirMount) : state.reservoirMount === 'not_applicable') ||
      !(state.reservoirMount === 'remote' ? state.hoseLengthCm === null || catalog.hoseLengthsCm.includes(state.hoseLengthCm) : state.hoseLengthCm === null)) {
      throw new TypeError('Unsupported coilover preview state');
    }
    resolveSpringRates(state);
  }

  function recommendation(state) {
    if (!state.intendedUse) return null;
    const setup = catalog.recommendationSetups[state.intendedUse];
    const core=catalog.cores.find(c=>c.setup===setup&&c.damper===(setup==='comfort'?'1m':state.damper)) || catalog.cores.find(c=>c.setup===setup);
    return core ? {setup:core.setup,damper:core.damper} : null;
  }

  function withValidDamper(state) {
    if (state.setup === 'comfort') state.damper = '1m';
    if (!catalog.cores.some(c=>c.setup===state.setup&&c.damper===state.damper)) {
      const core=catalog.cores.find(c=>c.setup===state.setup);
      if (!core) throw new TypeError('Disabled setup');
      state.damper=core.damper;
    }
    if (state.damper !== '2m') state.reservoirMount = 'not_applicable';
    else if (!catalog.reservoirMounts.includes(state.reservoirMount)) state.reservoirMount = catalog.reservoirMounts[0];
    if (state.reservoirMount !== 'remote') state.hoseLengthCm = null;
    return state;
  }

  function reduceSelection(state, action) {
    assertState(state);
    if (!action || typeof action !== 'object') return state;
    if (action.type === 'applyRecommendation') {
      const suggested = recommendation(state);
      return suggested ? withValidDamper({ ...state, ...suggested }) : state;
    }
    if (action.type !== 'set') return state;
    const { field, value } = action;
    const enumFields = {
      mode: catalog.modes, setup: catalog.setups, damper: catalog.dampers,
      customValving: catalog.customValving, frontMount: catalog.frontMounts, rearMount: catalog.rearMounts || [],
      color: catalog.colors, helperScope: catalog.helperScopes
    };
    if (has(enumFields, field)) {
      if (!validEnum(enumFields[field], value)) return state;
      if (field === 'damper' && state.setup === 'comfort' && value === '2m') return state;
      return withValidDamper({ ...state, [field]: value });
    }
    if (field === 'intendedUse') {
      return value === null || validEnum(catalog.intendedUses, value) ? { ...state, intendedUse: value } : state;
    }
    if (field === 'height') return has(catalog.heights, value) ? { ...state, height: value } : state;
    if (field === 'springPair') {
      if (value === 'custom') {
        if (!catalog.customSpringsEnabled) return state;
        const rates = resolveSpringRates(state);
        return { ...state, springPair: 'custom', frontSpringRateKgMm: retainedCustomRate('front',rates.frontKgMm), rearSpringRateKgMm: retainedCustomRate('rear',rates.rearKgMm),
          springLinked: false };
      }
      return has(catalog.springPairs, value) ? { ...state, springPair: value, frontSpringRateKgMm: null, rearSpringRateKgMm: null,
        springLinked: false } : state;
    }
    if (field === 'springLinked') return { ...state, springLinked: false };
    if (field === 'frontSpringRateKgMm' || field === 'rearSpringRateKgMm') {
      if (!catalog.customSpringsEnabled) return state;
      const permitted = field === 'frontSpringRateKgMm' ? catalog.springOptions.frontRatesKgMm : catalog.springOptions.rearRatesKgMm;
      if (!validRate(value) || !permitted.includes(value)) return state;
      const rates = resolveSpringRates(state);
      return { ...state, springPair: 'custom', springLinked: false,
        frontSpringRateKgMm: retainedCustomRate('front',rates.frontKgMm), rearSpringRateKgMm: retainedCustomRate('rear',rates.rearKgMm), [field]: value };
    }
    if (field === 'reservoirMount') {
      if (state.damper !== '2m' || !catalog.reservoirMounts.includes(value)) return state;
      return { ...state, reservoirMount: value, hoseLengthCm: value === 'remote' ? state.hoseLengthCm : null };
    }
    if (field === 'hoseLengthCm') return state.reservoirMount === 'remote' && (value === null || catalog.hoseLengthsCm.includes(value)) ? { ...state, hoseLengthCm: value } : state;
    if (field === 'notes') return typeof value === 'string' ? { ...state, notes: value.slice(0, 500) } : state;
    return state;
  }

  function resolvePricing(snapshot) {
    const base = {
      revision: 'base_usd_reference', currency: 'USD', priceContext: catalog.priceContext,
      variants: catalog.allCores.map(core => ({ key: core.key, variantId: core.variantId, priceCents: core.priceUsd * 100 })),
      charges: {custom_pro_valving:20000,height_oem:15000,height_extra_low:15000,height_lifted:23000,front_mount:5000,front_helpers:7500,front_rear_helpers:15000}
    };
    if (!snapshot) return base;
    if (!/^[A-Z]{3}$/.test(snapshot.currency) || !/^[A-Z]{2}$/.test(snapshot.market?.country || '') ||
        !['market_us_usd_unpublished_test','shopify_market_context'].includes(snapshot.priceContext) || typeof snapshot.revision !== 'string' ||
        !Array.isArray(snapshot.variants) || snapshot.variants.length !== catalog.allCores.length ||
        !Number.isFinite(Date.parse(snapshot.generatedAt)) || !Number.isFinite(Date.parse(snapshot.expiresAt)) ||
        Date.parse(snapshot.expiresAt) <= Date.parse(snapshot.generatedAt) ||
        catalog.allCores.some(core => !snapshot.variants.some(item => item.key === core.key && item.variantId === core.variantId && Number.isSafeInteger(item.priceCents) && item.priceCents > 0)) ||
        Object.keys(base.charges).some(key => !Number.isSafeInteger(snapshot.charges?.[key]) || snapshot.charges[key] < 0)) {
      throw new TypeError('Unsupported coilover market pricing');
    }
    return snapshot;
  }

  function derive(state, snapshot) {
    assertState(state);
    const pricing = resolvePricing(snapshot);
    const baseCore = catalog.cores.find(item => item.setup === state.setup && item.damper === state.damper);
    const core = {...baseCore, priceUsd: pricing.variants.find(item => item.key === baseCore.key).priceCents / 100};
    const charges = pricing.charges;
    const customPro = state.setup === 'custom' && state.customValving === 'pro';
    const effectiveValving = state.setup === 'pro_racing' || customPro ? 'pro' : state.setup === 'comfort' ? 'comfort' : 'standard';
    const springRates = resolveSpringRates(state);
    const catalogueSelection = springCatalogue.selection(catalog.productId, springRates);
    if (!catalog.springCatalogue.sourceConfirmed) { catalogueSelection.complete = false; catalogueSelection.missing = Object.entries(catalog.springCatalogue.axles).filter(([,a])=>a.required).map(([ax])=>'coil_'+ax+'_rate'); }
    const springLink = springLinkPolicy.describe(springRates, false);
    const standardRates = catalog.springOptions.standard;
    const rateRatio = springRates.frontKgMm!=null&&springRates.rearKgMm!=null ? springRates.frontKgMm / springRates.rearKgMm : null;
    const standardRatio = standardRates.frontKgMm!=null&&standardRates.rearKgMm!=null ? standardRates.frontKgMm / standardRates.rearKgMm : null;
    const springRateChangesPercent = {
      front: standardRates.frontKgMm==null ? null : (springRates.frontKgMm / standardRates.frontKgMm - 1) * 100,
      rear: standardRates.rearKgMm==null ? null : (springRates.rearKgMm / standardRates.rearKgMm - 1) * 100
    };
    const helperKey = state.helperScope === 'front_rear' ? 'front_rear_helpers' : 'front_helpers';
    const breakdown = [
      { key: 'core', amountUsd: core.priceUsd },
      { key: 'custom_pro_valving', amountUsd: customPro ? charges.custom_pro_valving / 100 : 0 },
      { key: 'height', amountUsd: state.height === 'lowered' ? 0 : charges['height_' + state.height] / 100 },
      { key: 'front_mount', amountUsd: state.frontMount === 'pillowball' && !catalog.includedFrontMount ? charges.front_mount / 100 : 0 },
      { key: 'rear_mount', amountUsd: state.rearMount === 'pillowball' && !catalog.includedRearMount ? (charges.rear_mount ?? charges.front_mount) / 100 : 0 },
      { key: helperKey, amountUsd: state.helperScope === 'none' ? 0 : charges[helperKey] / 100 }
    ];
    const productionFactors = state.damper === '2m' ? ['2m'] : [];
    const extendedProduction = state.damper === '2m';
    return {
      core,
      totalUsd: breakdown.reduce((sum, line) => sum + Math.round(line.amountUsd * 100), 0) / 100,
      breakdown,
      currency: pricing.currency,
      priceRevision: pricing.revision,
      pricingCurrent: pricing.contextAligned !== false && (!pricing.expiresAt || Date.now() < Date.parse(pricing.expiresAt)),
      optionPrices: {
        heights: Object.fromEntries(Object.keys(catalog.heights).map(key => [key, key === 'lowered' ? 0 : charges['height_' + key] / 100])),
        helpers: {none:0,front:charges.front_helpers/100,front_rear:charges.front_rear_helpers/100},
        frontMount: catalog.includedFrontMount ? 0 : charges.front_mount / 100,
        rearMount: catalog.includedRearMount ? 0 : (charges.rear_mount ?? charges.front_mount) / 100,
        customProValving: charges.custom_pro_valving / 100,
        damperUpgrade: (pricing.variants.find(item => item.key === 'sport_2m').priceCents - pricing.variants.find(item => item.key === 'sport_1m').priceCents) / 100
      },
      effectiveValving,
      productionFactors,
      manufacturingWorkingDays: extendedProduction ? { min: 15, max: 20 } : { min: 7, max: 12 },
      recommendation: recommendation(state),
      helperSuggestion: catalog.helperScopes.some(v=>v!=='none') && (state.setup === 'comfort' || state.intendedUse === 'daily' || state.intendedUse === 'rally'),
      availableOptions: {
        setups: catalog.setups,
        dampers: catalog.cores.filter(c=>c.setup===state.setup).map(c=>c.damper),
        customValving: catalog.customValving,
        heights: Object.keys(catalog.heights),
        frontMounts: catalog.frontMounts,
        helperScopes: catalog.helperScopes,
        rearMounts: catalog.rearMounts || [],
        springPairs: Object.keys(catalog.springPairs),
        springSelections: [...Object.keys(catalog.springPairs), ...(catalog.customSpringsEnabled && catalog.springAxles.length ? ['custom'] : [])],
        frontSpringRatesKgMm: springLink.enabled ? springLinkPolicy.frontRatesKgMm : catalog.springOptions.frontRatesKgMm,
        rearSpringRatesKgMm: springLink.enabled ? springLinkPolicy.rearRatesKgMm : catalog.springOptions.rearRatesKgMm,
        reservoirMounts: state.damper === '2m' ? catalog.reservoirMounts : [],
        hoseLengthsCm: state.reservoirMount === 'remote' ? catalog.hoseLengthsCm : [],
        colors: catalog.colors
      },
      visibleControls: {
        technical: state.mode === 'expert',
        customSprings: catalog.customSpringsEnabled && catalog.springAxles.length>0,
        customValving: state.mode === 'expert' && state.setup === 'custom',
        reservoirMount: state.damper === '2m',
        hoseLength: state.reservoirMount === 'remote'
      },
      springRates,
      springLink,
      springChangePercent: springRateChangesPercent.front,
      springRateChangesPercent,
      springRateRatio: {
        selected: rateRatio, standard: standardRatio,
        relativeChangePercent: rateRatio===null || standardRatio===null ? null : (rateRatio / standardRatio - 1) * 100,
        matchesStandard: springLink.matchesStandard
      },
      springSuggestion: (state.intendedUse === 'daily' || state.setup === 'comfort') && catalog.springPairs.softer_pair_v1 ? 'softer_pair_v1' : (state.intendedUse === 'track' || state.setup === 'pro_racing') && catalog.springPairs.firmer_pair_v1 ? 'firmer_pair_v1' : 'standard_pair_v1',
      requiredMissing: [...(state.reservoirMount === 'remote' && state.hoseLengthCm === null ? ['coil_hose'] : []), ...catalog.springAxles.filter(ax=>springRates[ax+'KgMm']==null).map(ax=>'coil_'+ax+'_rate'), ...catalogueSelection.missing],
      springCatalogueSelection: catalogueSelection,
      configurationComplete: catalogueSelection.complete && (state.reservoirMount !== 'remote' || state.hoseLengthCm !== null) && catalog.springAxles.every(ax=>springRates[ax+'KgMm']!=null),
      orderingEnabled: false,
      priceContext: pricing.priceContext,
      notices: ['preview_only', 'special_features_may_add_time']
    };
  }

  function mountInfo(axle,key) {
    const mount=catalog.mountDetails?.[axle]?.options.find(o=>o.key===key);
    if(!mount)throw new TypeError('Unavailable mount for this axle');
    const labelKey=key==='pillowball' ? mount.camberAdjustable===true ? 'mount_with_camber' : mount.camberAdjustable===false ? 'mount_without_camber' : 'mount_camber_unconfirmed' :
      key==='rubber' ? 'mount_rubber_title' : key==='reuse_oem' ? 'mount_reuse_oem_title' : 'mount_none';
    const noteKey=key==='pillowball' ? mount.camberAdjustable===true ? 'mount_with_camber_note' : mount.camberAdjustable===false ? 'mount_without_camber_note' : 'mount_camber_unconfirmed_note' :
      key==='rubber' ? 'mount_rubber_note' : key==='reuse_oem' ? 'mount_reuse_oem_note' : 'mount_none_note';
    return {...mount,labelKey,noteKey};
  }

  function fromVariantId(id) {
    const core = catalog.cores.find(v=>v.variantId === String(id));
    if(!core && /^[1-9][0-9]*$/.test(String(catalog.legacyVariantId)) && String(id)===String(catalog.legacyVariantId))return initialState();
    if (!core) throw new TypeError('Variant does not belong to this application');
    return withValidDamper({...initialState(),setup:core.setup,damper:core.damper});
  }

  return Object.freeze({ mountInfo, fromVariantId, initialState, reduceSelection, derive, resolveSpringRates, createSpringLinkPolicy, springLinkPolicy, catalog });
});

